<template>
  <div class="demo-scan-view">
    <div class="demo-banner">
      <span>🧪 UI Preview Mode - Mock Data</span>
    </div>

    <div class="scan-animals-view">
      <div class="audit-container">
        <div class="view-header">
          <h1 class="view-title">Scan Animals</h1>
          <p class="cage-info">Cage #12345 | Room 201A</p>
        </div>

        <div class="main-content-layout">
          <div class="scanner-column">
            <div class="scanner-section">
              <div class="mock-scanner">
                <p>Scanner (Demo Mode)</p>
                <input
                  v-model="scanInput"
                  placeholder="Type barcode & press Enter"
                  @keyup.enter="handleMockScan"
                  class="mock-input"
                />
                <button @click="handleMockScan" class="scan-btn">Scan</button>
              </div>
            </div>

            <div class="status-container">
              <div v-if="lastScanMessage" class="status-card" :class="lastScanType">
                <div class="status-content">
                  <strong>{{ lastScanMessage }}</strong>
                </div>
              </div>
            </div>
          </div>

          <div class="animals-column">
            <AnimalScanList :expected-animals="expectedAnimals" :scanned-animals="scannedAnimals" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import AnimalScanList from '../components/audit/AnimalScanList.vue';
import type { ExpectedAnimal, ScannedAnimal } from '../stores/audit.store';

// Mock data with enhanced fields
const expectedAnimals = ref<ExpectedAnimal[]>([
  {
    id: 'animal-001',
    rapidTagBarcode: 'ABC123',
    color: 'Red',
    studyNumber: 'STD-2024-001',
    studyGroup: 'Control',
    primeAnimalId: 'P-4521',
    homeCageNumber: '12345',
    location: 'Room 201A',
  },
  {
    id: 'animal-002',
    rapidTagBarcode: 'DEF456',
    color: 'Blue',
    studyNumber: 'STD-2024-001',
    studyGroup: 'Treatment',
    primeAnimalId: 'P-4522',
    homeCageNumber: '12345',
    location: 'Room 201A',
  },
  {
    id: 'animal-003',
    rapidTagBarcode: 'GHI789',
    color: 'Green',
    studyNumber: 'STD-2024-001',
    studyGroup: 'Treatment',
    primeAnimalId: 'P-4523',
    homeCageNumber: '12345',
    location: 'Room 201A',
  },
  {
    id: 'animal-004',
    rapidTagBarcode: 'JKL012',
    color: 'Yellow',
    studyNumber: 'STD-2024-001',
    studyGroup: 'Observation',
    primeAnimalId: 'P-4524',
    homeCageNumber: '12345',
    location: 'Room 201A',
  },
  {
    id: 'animal-005',
    rapidTagBarcode: 'MNO345',
    color: 'Purple',
    studyNumber: 'STD-2024-001',
    studyGroup: 'Control',
    primeAnimalId: 'P-4525',
    homeCageNumber: '12345',
    location: 'Room 201A',
  },
]);

const scannedAnimals = ref<ScannedAnimal[]>([
  {
    rapidTagBarcode: 'ABC123',
    scannedAt: new Date().toISOString(),
    isExpected: true,
    animalId: 'animal-001',
  },
]);

const scanInput = ref('');
const lastScanMessage = ref('');
const lastScanType = ref('');

function handleMockScan() {
  const barcode = scanInput.value.toUpperCase().trim();
  if (!barcode) return;

  // Check if already scanned
  const alreadyScanned = scannedAnimals.value.some((a) => a.rapidTagBarcode === barcode);
  if (alreadyScanned) {
    lastScanMessage.value = `${barcode} already scanned`;
    lastScanType.value = 'warning-banner';
    scanInput.value = '';
    return;
  }

  // Check if expected
  const expected = expectedAnimals.value.find((a) => a.rapidTagBarcode === barcode);

  scannedAnimals.value.push({
    rapidTagBarcode: barcode,
    scannedAt: new Date().toISOString(),
    isExpected: !!expected,
    animalId: expected?.id,
  });

  if (expected) {
    lastScanMessage.value = `✓ ${barcode} scanned successfully`;
    lastScanType.value = 'success-banner';
  } else {
    lastScanMessage.value = `⚠ ${barcode} - Unexpected animal`;
    lastScanType.value = 'warning-banner';
  }

  scanInput.value = '';
}
</script>

<style scoped>
.demo-scan-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #f6f8fb 0%, #e5ebf1 100%);
}

.demo-banner {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  text-align: center;
  padding: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
}

.scan-animals-view {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 1rem;
}

.audit-container {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.view-header {
  text-align: left;
}

.view-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.cage-info {
  color: #6b7280;
  margin: 0;
  font-size: 1rem;
}

.main-content-layout {
  display: flex;
  gap: 2rem;
  min-height: 500px;
}

.scanner-column {
  flex: 0 0 40%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mock-scanner {
  background: #f3f4f6;
  padding: 1.5rem;
  border-radius: 0.75rem;
  text-align: center;
}

.mock-scanner p {
  margin: 0 0 1rem 0;
  font-weight: 600;
  color: #374151;
}

.mock-input {
  width: 100%;
  padding: 1rem;
  font-size: 1.25rem;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  border: 2px solid #d1d5db;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
}

.mock-input:focus {
  outline: none;
  border-color: #667eea;
}

.scan-btn {
  width: 100%;
  padding: 0.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}

.scan-btn:hover {
  opacity: 0.9;
}

.status-container {
  min-height: 4rem;
}

.status-card {
  padding: 1rem;
  border-radius: 0.5rem;
  text-align: center;
}

.success-banner {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #10b981;
}

.warning-banner {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #f59e0b;
}

.animals-column {
  flex: 1;
  min-height: 0;
  max-height: 600px;
  overflow: hidden;
}

@media (max-width: 900px) {
  .main-content-layout {
    flex-direction: column;
  }

  .scanner-column {
    flex: none;
  }

  .animals-column {
    max-height: 400px;
  }
}
</style>
